import React, { useState, useEffect } from 'react';
import { useContactForm } from '../context/ContactFormContext';
import logo from './assets/AutometaLogo.png';

const Navbar = () => {
  const { openForm } = useContactForm();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('');

  useEffect(() => {
    // Check for saved theme preference or default to light mode
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);

      // Determine active section based on scroll position
      const sections = ['hero', 'features', 'whatwedo', 'videoads', 'testimonial', 'thenumbers', 'getstarted'];
      const sectionElements = sections.map(id => document.getElementById(id));
      
      let current = '';
      sectionElements.forEach((section, index) => {
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            current = sections[index];
          }
        }
      });

      if (current) {
        setActiveLink(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const navLinks = [
    { name: 'Features', href: '#features', id: 'features' },
    { name: 'What We Do', href: '#whatwedo', id: 'whatwedo' },
    { name: 'Video Ads', href: '#videoads', id: 'videoads' },
    { name: 'Stats', href: '#thenumbers', id: 'thenumbers' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 text-ink px-4 sm:px-6 lg:px-8 py-4 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md border-b border-black/5' : 'bg-white/80 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo - darkened so visible on light navbar background */}
        <div className="flex items-center">
          <a href="/" className="flex items-center gap-2">
            <img 
              src={logo} 
              alt="Autometa Studio" 
              className="h-8 sm:h-10 md:h-12 w-auto brightness-0"
            />
            <span className="text-sm sm:text-base md:text-lg font-bold text-ink">
              Autometa Studio
            </span>
          </a>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-8">
          {/* Nav Links */}
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className={`transition-colors ${
                activeLink === link.id
                  ? 'text-brand font-semibold'
                  : 'text-ink-muted hover:text-brand'
              }`}
            >
              {link.name}
            </a>
          ))}
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center space-x-4">
          {/* Contact Sales Button - Desktop */}
          <button 
            onClick={openForm}
            className="hidden lg:block bg-brand hover:bg-brand-hover text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-sm"
          >
            Contact Sales
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-brand-light transition-colors text-ink"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden mt-4 pb-4 border-t border-black/10">
          <div className="flex flex-col space-y-4 pt-4">
            {/* Mobile Links */}
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`transition-colors ${
                  activeLink === link.id
                    ? 'text-brand font-semibold'
                    : 'text-ink-muted hover:text-brand'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}

            {/* Contact Sales Button - Mobile */}
            <button 
              className="bg-brand hover:bg-brand-hover text-white px-6 py-2 rounded-lg font-medium transition-colors w-full shadow-sm"
              onClick={() => {
                setIsMenuOpen(false);
                openForm();
              }}
            >
              Contact Sales
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;

